import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import { Plus, Edit, Trash2, MessageSquare, Search, Eye } from 'lucide-react';

const Comunicados = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [comunicados, setComunicados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingComunicado, setEditingComunicado] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    tipo: '',
    prioridade: '',
    titulo: ''
  });
  const [formData, setFormData] = useState({
    comunicado_titulo: '',
    comunicado_conteudo: '',
    comunicado_tipo: 'geral',
    comunicado_prioridade: 'media',
    comunicado_status: 'rascunho',
    comunicado_data_publicacao: '',
    comunicado_data_expiracao: ''
  });

  useEffect(() => {
    if (condominioSelecionado) {
      loadComunicados();
    }
  }, [condominioSelecionado]);

  const loadComunicados = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const params = new URLSearchParams();
      params.append('condominio_id', condominioId);
      
      if (filters.status) params.append('status', filters.status);
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.prioridade) params.append('prioridade', filters.prioridade);
      if (filters.titulo) params.append('titulo', filters.titulo);

      const response = await fetch(`http://localhost:5000/api/comunicados?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComunicados(data.data.comunicados);
      }
    } catch (error) {
      console.error('Erro ao carregar comunicados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const condominioId = getCondominioId();
      const dadosParaEnvio = {
        ...formData,
        condominio_id: condominioId,
        comunicado_data_publicacao: formData.comunicado_data_publicacao || null,
        comunicado_data_expiracao: formData.comunicado_data_expiracao || null
      };

      const url = editingComunicado 
        ? `http://localhost:5000/api/comunicados/${editingComunicado.comunicado_id}`
        : 'http://localhost:5000/api/comunicados';
      
      const method = editingComunicado ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(dadosParaEnvio)
      });

      const result = await response.json();

      if (result.success) {
        setShowModal(false);
        resetForm();
        loadComunicados();
      } else {
        setErrorMessage(result.message || 'Erro ao salvar comunicado');
      }
    } catch (error) {
      setErrorMessage('Erro ao salvar comunicado');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (comunicado) => {
    setEditingComunicado(comunicado);
    setFormData({
      comunicado_titulo: comunicado.comunicado_titulo,
      comunicado_conteudo: comunicado.comunicado_conteudo,
      comunicado_tipo: comunicado.comunicado_tipo,
      comunicado_prioridade: comunicado.comunicado_prioridade,
      comunicado_status: comunicado.comunicado_status,
      comunicado_data_publicacao: comunicado.comunicado_data_publicacao ? 
        new Date(comunicado.comunicado_data_publicacao).toISOString().split('T')[0] : '',
      comunicado_data_expiracao: comunicado.comunicado_data_expiracao ? 
        new Date(comunicado.comunicado_data_expiracao).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este comunicado?')) return;

    try {
      const response = await fetch(`http://localhost:5000/api/comunicados/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (result.success) {
        loadComunicados();
      } else {
        alert('Erro ao excluir comunicado: ' + result.message);
      }
    } catch (error) {
      alert('Erro ao excluir comunicado');
    }
  };

  const resetForm = () => {
    setFormData({
      comunicado_titulo: '',
      comunicado_conteudo: '',
      comunicado_tipo: 'geral',
      comunicado_prioridade: 'media',
      comunicado_status: 'rascunho',
      comunicado_data_publicacao: '',
      comunicado_data_expiracao: ''
    });
    setEditingComunicado(null);
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    loadComunicados();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      tipo: '',
      prioridade: '',
      titulo: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'rascunho': 'bg-gray-100 text-gray-800',
      'publicado': 'bg-green-100 text-green-800',
      'arquivado': 'bg-blue-100 text-blue-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadeBadge = (prioridade) => {
    const prioridadeMap = {
      'baixa': 'bg-blue-100 text-blue-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'alta': 'bg-orange-100 text-orange-800',
      'urgente': 'bg-red-100 text-red-800'
    };
    return prioridadeMap[prioridade] || 'bg-gray-100 text-gray-800';
  };

  const getTipoBadge = (tipo) => {
    const tipoMap = {
      'geral': 'bg-blue-100 text-blue-800',
      'manutencao': 'bg-orange-100 text-orange-800',
      'seguranca': 'bg-red-100 text-red-800',
      'evento': 'bg-green-100 text-green-800',
      'aviso': 'bg-yellow-100 text-yellow-800',
      'informacao': 'bg-blue-100 text-blue-800'
    };
    return tipoMap[tipo] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Comunicados</h1>
        </div>
        {isAdministrador() && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Comunicado
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="form-label">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">Todos</option>
              <option value="rascunho">Rascunho</option>
              <option value="publicado">Publicado</option>
              <option value="arquivado">Arquivado</option>
            </select>
          </div>
          <div>
            <label className="form-label">Tipo</label>
            <select
              name="tipo"
              value={filters.tipo}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">Todos</option>
              <option value="geral">Geral</option>
              <option value="manutencao">Manutenção</option>
              <option value="seguranca">Segurança</option>
              <option value="evento">Evento</option>
              <option value="aviso">Aviso</option>
              <option value="informacao">Informação</option>
            </select>
          </div>
          <div>
            <label className="form-label">Prioridade</label>
            <select
              name="prioridade"
              value={filters.prioridade}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">Todas</option>
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div>
            <label className="form-label">Título</label>
            <input
              type="text"
              name="titulo"
              value={filters.titulo}
              onChange={handleFilterChange}
              placeholder="Buscar por título"
              className="form-input"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="btn btn-primary"
            >
              <Search className="h-4 w-4 mr-2" />
              Filtrar
            </button>
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
            >
              Limpar
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Publicação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visualizações
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : comunicados.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhum comunicado encontrado
                  </td>
                </tr>
              ) : (
                comunicados.map((comunicado) => (
                  <tr key={comunicado.comunicado_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {comunicado.comunicado_titulo}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {comunicado.comunicado_conteudo}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoBadge(comunicado.comunicado_tipo)}`}>
                        {comunicado.comunicado_tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPrioridadeBadge(comunicado.comunicado_prioridade)}`}>
                        {comunicado.comunicado_prioridade}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(comunicado.comunicado_status)}`}>
                        {comunicado.comunicado_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(comunicado.comunicado_data_publicacao)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {comunicado.comunicado_visualizacoes || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {isAdministrador() && (
                          <>
                            <button
                              onClick={() => handleEdit(comunicado)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(comunicado.comunicado_id)}
                              className="text-red-600 hover:text-red-900"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar comunicados-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingComunicado ? 'Editar Comunicado' : 'Novo Comunicado'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingComunicado(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    name="comunicado_tipo"
                    value={formData.comunicado_tipo}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="geral">Geral</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="seguranca">Segurança</option>
                    <option value="evento">Evento</option>
                    <option value="aviso">Aviso</option>
                    <option value="informacao">Informação</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridade *
                  </label>
                  <select
                    name="comunicado_prioridade"
                    value={formData.comunicado_prioridade}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    name="comunicado_status"
                    value={formData.comunicado_status}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="rascunho">Rascunho</option>
                    <option value="publicado">Publicado</option>
                    <option value="arquivado">Arquivado</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  name="comunicado_titulo"
                  value={formData.comunicado_titulo}
                  onChange={handleInputChange}
                  required
                  maxLength="200"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conteúdo *
                </label>
                <textarea
                  name="comunicado_conteudo"
                  value={formData.comunicado_conteudo}
                  onChange={handleInputChange}
                  required
                  maxLength="5000"
                  rows="8"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Publicação
                  </label>
                  <input
                    type="date"
                    name="comunicado_data_publicacao"
                    value={formData.comunicado_data_publicacao}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Expiração
                  </label>
                  <input
                    type="date"
                    name="comunicado_data_expiracao"
                    value={formData.comunicado_data_expiracao}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comunicados;

// Estilos CSS personalizados para o modal de Comunicados
const comunicadosStyles = `
  .comunicados-modal .input-field {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;
    background-color: #f9fafb;
  }
  
  .comunicados-modal .input-field:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .comunicados-modal .input-field:hover {
    border-color: #d1d5db;
    background-color: #ffffff;
  }
  
  .comunicados-modal .btn-primary {
    background-color: #3b82f6;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .comunicados-modal .btn-primary:hover:not(:disabled) {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  .comunicados-modal .btn-primary:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
  
  .comunicados-modal .btn-secondary {
    background-color: #6b7280;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .comunicados-modal .btn-secondary:hover {
    background-color: #4b5563;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
  }
`;

// Adicionar estilos ao documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = comunicadosStyles;
  document.head.appendChild(styleSheet);
}
